import { isInIframe } from '@shared/utils/isInIframe';
import { deriveWebsiteColorsFromEventDesign } from '@shared/utils/websiteDesign';
import { GuestSiteEventDesign, LayoutConfigAloha, LayoutThemeOverrides, ApplicatorConfigVariantsAloha } from '../layout.types';
import chroma from 'chroma-js';
import { useStyleApplicator } from '../../common/utils/useStyleApplicator';
import { Theme, createTheme } from '@withjoy/joykit';
import { useMediaQuery } from '@withjoy/joykit/utils';
import { useMemo } from 'react';
import globalWindow from '@shared/core/globals';
import { Colors, createGlobalStyle, pxToRem } from '@withjoy/joykit/theme';
import memoizeOne from 'memoize-one';
import { CreateThemeArgs } from '@withjoy/joykit/theme';
import { mergeTypograhyThemeOverrides } from '../../common/theming';
import { useFeatureValue, useIsFeatureEnabled } from '@shared/core/featureFlags';
import { getActiveColor, getHoverColor } from '../layout.utils';

const isIframed = isInIframe();

const THEME_DEFAULTS: CreateThemeArgs['defaults'] = {
  breakpointWidths: [isIframed ? 375 : 480, 960, 1024, 1440, 1920],
};

export const getBrannanMobileMediaQuery = (theme: Theme) => theme.mediaQueries.between(0, { breakpointAlias: 'sm4' });

export const MAX_CONTENT_WIDTH = 376;

export const CONTENT_BLOCK_MAX_WIDTH = {
  _: 'auto',
  md: pxToRem(440),
  lg: pxToRem(480),
};

export const CONTAINER_BLOCK_PADDING_X = {
  _: 7,
  sm2: 10,
};

const generateThemableColorValues = memoizeOne((color?: string): DeepPartial<Colors> => {
  if (color) {
    const colorHover = getHoverColor(color);
    const colorActive = getActiveColor(color);

    return {
      linkVisited: chroma(color).darken(0.3).hex(),
      linkText: color,
      linkHover: colorHover,
      linkActive: colorActive,

      buttonPrimaryText: '#FFFFFF',
      buttonPrimaryFill: color,
      buttonPrimaryHover: colorHover,
      buttonPrimaryActive: colorActive,

      buttonOutlinePrimaryText: color,
      buttonOutlinePrimaryTextHover: colorHover,
      buttonOutlinePrimaryTextPress: colorActive,
      buttonOutlinePrimaryFillHover: chroma(color).alpha(0.1).css(),
      buttonOutlinePrimaryFillPress: chroma(color).alpha(0.2).css(),

      scrollbarHandle: `rgba(${chroma(color).alpha(0.5).rgba().join(', ')})`,
    };
  }
  return {};
});

export const getThemeOverrides = (overrides: LayoutThemeOverrides = {}): DeepPartial<Theme> => {
  const { eventFontFamily, textTransform = 'none' } = overrides.typography || {};
  const { primaryFillColor } = overrides.colors || {};

  const themableColors: DeepPartial<Colors> = generateThemableColorValues(primaryFillColor);
  const typography = mergeTypograhyThemeOverrides({
    primaryFontFamily: eventFontFamily,
    variants: {
      body4: {
        fontWeight: 400,
        fontSize: pxToRem(17),
        lineHeight: 1.5,
        letterSpacing: '0em',
      },
      label2: {
        fontWeight: 400,
        fontSize: pxToRem(13),
        lineHeight: 1.4,
        letterSpacing: '0em',
      },

      hed2: {
        fontWeight: 700,
        fontSize: pxToRem(17),
        lineHeight: 1.36,
        letterSpacing: '0.02em',
      },
      hed3: {
        fontWeight: 500,
        fontSize: pxToRem(20),
        lineHeight: 1.36,
        letterSpacing: '0em',
      },
      hed4: {
        fontWeight: 400,
        fontSize: pxToRem(20),
        lineHeight: 1.4,
        letterSpacing: '0.16em',
        textTransform: 'uppercase',
      },
      hed5: {
        fontSize: pxToRem(24),
        lineHeight: 1.3,
      },
      hed6: {
        fontSize: pxToRem(24),
        lineHeight: 1.3,
        letterSpacing: '-0.01em',
      },

      display1: {
        fontFamily: eventFontFamily,
        lineHeight: 1.23,
        textTransform,
      },
      display2: {
        fontFamily: eventFontFamily,
        textTransform,
      },
      display3: {
        fontFamily: eventFontFamily,
        textTransform,
      },
      display4: {
        fontFamily: eventFontFamily,
        textTransform,
      },
      display5: {
        fontFamily: eventFontFamily,
        textTransform,
      },
      display6: {
        fontFamily: eventFontFamily,
        textTransform,
      },
      display7: {
        fontFamily: eventFontFamily,
        textTransform,
        lineHeight: 1.25,
      },
      display8: {
        fontFamily: eventFontFamily,
        textTransform,
        lineHeight: 1.25,
      },
      display9: {
        fontFamily: eventFontFamily,
        textTransform,
        lineHeight: 1.25,
      },
      display10: {
        fontFamily: eventFontFamily,
        textTransform,
        lineHeight: 1.25,
      },
      display11: {
        fontFamily: eventFontFamily,
        textTransform,
        lineHeight: 1.25,
      },
    },
  });
  return {
    typography,
    colors: {
      ...themableColors,
    },
  };
};

export const alohaConfig: LayoutConfigAloha = {
  applicatorHtmlProps: {
    simple: {
      prop: { 'data-layer': 'simple' },
      selector: '[data-layer="simple"]',
    },
  },
  theme: createTheme({ defaults: THEME_DEFAULTS, overrides: getThemeOverrides() }), // extendTheme(getThemeOverrides()),
  configureThemeOverrides: eventDesign => {
    const { font } = eventDesign;
    const { accentColor } = deriveWebsiteColorsFromEventDesign(eventDesign, eventDesign.theme);
    const overrides = getThemeOverrides({
      typography: { eventFontFamily: font.fontFamily, textTransform: font.textTransform },
      colors: { primaryFillColor: accentColor },
    });
    return createTheme({ defaults: THEME_DEFAULTS, overrides });
  },
  fonts: {
    google: ['greatVibes'],
  },
  previewEventHandlers: {
    onTemplateChange: () => {},
    onLayoutChange: ({ history, eventPageRoutes }) => {
      if (eventPageRoutes.welcome) {
        history.push({ pathname: eventPageRoutes.welcome.path });
      }
      globalWindow.scrollTo?.(0, 0);
    },
    onThemeChange: () => {
      globalWindow.scrollTo?.(0, 0);
    },
    onFontChange: () => {
      globalWindow.scrollTo?.(0, 0);
    },
    onGraphicAccentChange: () => {
      const el = globalWindow.document?.getElementById('graphic-accent');
      el?.scrollIntoView();
    },
    onPrimaryFillColorChange: ({ history, eventPageRoutes }) => {
      if (eventPageRoutes.schedule) {
        const historySub = history.listen(() => {
          globalWindow.setTimeout(() => {
            const el = globalWindow.document?.getElementById('schedule');
            el?.scrollIntoView();
          }, 500);
          historySub();
        });
        history.push({ pathname: eventPageRoutes.schedule.path });
        return [historySub];
      }
      return [];
    },
  },
};

////////////////////////////////////////////////////////////////

export const getAlohaApplicatorConfig = (eventDesign: NonNullable<GuestSiteEventDesign>, isBypassWebsiteThemeCustomizationEnabled: boolean): ApplicatorConfigVariantsAloha => {
  const derivedColors = deriveWebsiteColorsFromEventDesign(eventDesign, eventDesign.theme);

  const applicatorConfig: ApplicatorConfigVariantsAloha = {
    simple: {
      customBackgroundColor: {
        isEnabled: true,
        // If we roll back the feature flag, we'll need to adjust the text color
        // if the user has manually changed the text color
        textColor: chroma(derivedColors.artBackgroundFontColor).rgba(),
        backgroundColor: chroma(derivedColors.artBackgroundColor).rgba(),
        bypassThemeCustomization: isBypassWebsiteThemeCustomizationEnabled,
      },
    },
  };

  return applicatorConfig;
};

////////////////////////////////////////////////////////////////

export const useAlohaDesign = (_eventHandle: string, eventDesign: GuestSiteEventDesign) => {
  const { customCss } = eventDesign || {};
  const themeId = eventDesign?.theme.themeId;
  const isSmallScreen = useMediaQuery(getBrannanMobileMediaQuery);
  const [isBypassWebsiteThemeCustomizationEnabled] = useIsFeatureEnabled({ eventFeatureFlagKey: 'bypassWebsiteThemeCustomization' });

  // FeatureFlag => wait for StyleApplicator https://github.com/joylifeinc/joy-web/pull/3214
  const styledApplicatorLoading = useFeatureValue('enableLoadingStyleApplicator');

  // Get applicator config based on user design preferences
  const applicatorConfig = useMemo(() => {
    return eventDesign ? getAlohaApplicatorConfig(eventDesign, isBypassWebsiteThemeCustomizationEnabled) : undefined;
  }, [eventDesign, isBypassWebsiteThemeCustomizationEnabled]);

  const { loading } = useStyleApplicator(
    styleApplicator => {
      if (themeId) {
        const { simple } = alohaConfig.applicatorHtmlProps;

        // Apply styles to frames
        return [styleApplicator.applyCompositionSprites(simple.selector, themeId, 'simple', 'content', applicatorConfig?.simple)];
      }
      return [];
    },
    [themeId, applicatorConfig],
  );

  return { applicatorConfig, isSmallScreen, loading: styledApplicatorLoading ? loading : false, CustomStyle: customCss ? createGlobalStyle`${customCss}` : null };
};
